import { ConsoleLogger, HttpServiceBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { MS_PER_MINUTE, getMaxOfDates, substractSeconds } from '@skylab-shared/utils';
import { Transform } from 'class-transformer';

export type RefreshIntervalType = 'default' | 'imports' | 'emailStatus' | 'onlineReceiver' | 'dataExchange' | 'onStartup' | 'documentKsefStatus' | 'ksefReceivingEnded' | 'ksefConfiguration' | 'preparingData' | 'ksefConfigurationStatusUpdate';
export type EventNotificationType =
    | 'NotificationAdded'
    | 'EmailStatusUpdated'
    | 'NexoDdkStatusUpdated'
    | 'JpkGenerationStatusUpdated'
    | 'EppGenerationStatusUpdated'
    | 'ClientsImported'
    | 'ProductsImported'
    | 'PackageOfDocumentsGenerationStatusUpdated'
    | 'KsefProcessingEnded'
    | 'KsefMonitorStatusChanged'
    | 'DocumentKsefStatusUpdated'
    | 'KsefConfigurationStatusUpdate'
    | 'PreparingData';

export class EventNotification<TData = unknown> {
    public readonly kind: EventNotificationType;
    public readonly properties?: TData;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly time: Date;

}

type EventNotificationsObserver = (events: EventNotification[]) => void;

export class EventNotificationsServiceBase {
    private readonly url = TaggedUrl.join('events', [Tags.Api, Tags.OmitMessage]);
    private readonly observers: EventNotificationsObserver[] = [];

    private logger: ConsoleLogger;

    private lastCheckDate = substractSeconds(new Date(), 5); // add seconds, because of an event can be added before s123 page loads, e.g. notification about license expiration
    private lastEventDate: Date;

    private intervalsQueue: RefreshIntervalType[] = [];
    private currentIntervalType: RefreshIntervalType;

    private readonly intervals: {[K in RefreshIntervalType]: [number | null, number]}  = { // [n - duration, m - interval] - repeat every m minutes for n minutes (time in minutes)
        default: [null, 30],
        emailStatus: [15, 1],
        documentKsefStatus: [15, 1],
        ksefReceivingEnded: [15, 1],
        imports: [10, 2],
        dataExchange: [10, 0.5],
        ksefConfiguration: [5, 0.3],
        onlineReceiver: [3, 1],
        preparingData: [3, 0.5],
        ksefConfigurationStatusUpdate: [1, 0.3],
        onStartup: [1 / 3, 1 / 12] // every five seconds for twenty seconds
    };

    public constructor() {
        this.logger = new ConsoleLogger(this.constructor.name);
        this.convertIntervalsToMiliseconds();
        this.refreshInterval();
    }

    private async getEventsAsync(): Promise<void> {
        try {
            const checkDate = new Date();

            const filterNewEvents = (item: EventNotification) => this.lastEventDate ? item.time > this.lastEventDate : item.time >= this.lastCheckDate;

            const { data } = await HttpServiceBase.get<EventNotification[]>(this.url, {dto: EventNotification});
            const events = data.map(elem => ({...elem, time: new Date(elem.time)}));
            const newEvents = events.filter(filterNewEvents);
            this.notifyObservers(newEvents);

            this.lastEventDate = getMaxOfDates(...events.map(elem => elem.time))!;
            this.lastCheckDate = checkDate;
        } catch (err) {
            this.logger.error(err);
        }
    }

    private intervalHandler: ReturnType<typeof setInterval>;
    public refreshInterval(type: RefreshIntervalType = 'default') : void {
        if (this.intervalHandler) {
            this.getEventsAsync();
        }

        if (type === 'default' && this.intervalsQueue.length > 0) {
            type = this.intervalsQueue.shift()!;
        }

        if (this.currentIntervalType !== 'default' && type !== 'default') {
            const [ currentDuration ] = this.intervals[this.currentIntervalType];
            const [ newDuration ] = this.intervals[type];
            if (newDuration! < currentDuration!) {
                this.intervalsQueue.push(type);
                return;
            }
        }

        let intervalCounter = 1;
        const [duration, interval] = this.intervals[type];
        const repetitions = duration ? this.getFrequency(duration, interval) : null;
        this.currentIntervalType = type;

        clearInterval(this.intervalHandler);
        this.intervalHandler = setInterval(async () => {
            await this.getEventsAsync();

            if (intervalCounter === repetitions) {
                clearInterval(this.intervalHandler);
                this.currentIntervalType = 'default';
                this.refreshInterval();
            }

            intervalCounter++;
        }, interval);
    }

    public addObserver(observer: EventNotificationsObserver): void {
        this.observers.push(observer);
    }

    private notifyObservers(types: EventNotification[]): void {
        this.observers.forEach(observer => observer(types));
    }

    private convertIntervalsToMiliseconds(): void {
        Object.keys(this.intervals).forEach(key => this.intervals[key as RefreshIntervalType].forEach((v, i) => this.intervals[key as RefreshIntervalType][i] = v! * MS_PER_MINUTE));
    }

    private getFrequency(time: number, duration: number): number {
        return Math.floor(time / duration);
    }

}

export const EventNotificationsServiceHandler = new EventNotificationsServiceBase();
